import React, { useState, useEffect, useCallback } from 'react';
import { getSalesHeaders as getHeaders } from '../../utils/salesAuth';

const API = process.env.REACT_APP_BACKEND_URL;

const SCORE_CONFIG = {
  NONE: { bg: '#f1f5f9', color: '#64748b', label: 'No Expansion', icon: '—' },
  LOW: { bg: '#fef9c3', color: '#a16207', label: 'Low', icon: '○' },
  MEDIUM: { bg: '#dbeafe', color: '#2563eb', label: 'Medium', icon: '◑' },
  HIGH: { bg: '#dcfce7', color: '#16a34a', label: 'High', icon: '●' },
  MAJOR_PROJECT: { bg: '#fce7f3', color: '#be185d', label: 'Major Project', icon: '★' },
};

const REL_COLORS = {
  HIGH: { bg: '#dcfce7', color: '#16a34a' },
  MEDIUM: { bg: '#dbeafe', color: '#2563eb' },
  LOW: { bg: '#fef9c3', color: '#a16207' },
};

const FEEDBACK_OPTIONS = [
  { key: 'useful', label: 'Useful', icon: '✓' },
  { key: 'not_relevant', label: 'Not Relevant', icon: '✗' },
  { key: 'already_discussed', label: 'Already Discussed', icon: '↻' },
  { key: 'not_interested', label: 'Not Interested', icon: '—' },
  { key: 'future_project', label: 'Future Project', icon: '◷' },
  { key: 'added_to_scope', label: 'Added to Scope', icon: '+' },
];

export default function ExpansionPanel({ oppId }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showProject, setShowProject] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [editingProject, setEditingProject] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingProject, setSavingProject] = useState(false);
  const [projectSaved, setProjectSaved] = useState(false);

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sales/opportunities/${oppId}/expansion`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAnalysis(data);
        // Build feedback map
        const fbMap = {};
        (data.feedback || []).forEach(fb => { fbMap[fb.recommendation_index] = fb.feedback_type; });
        setFeedbackGiven(fbMap);
      }
    } finally { setLoading(false); }
  }, [oppId]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/sales/opportunities/${oppId}/expansion/analyze`, {
        method: 'POST', credentials: 'include', headers: getHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setAnalysis(data);
        setFeedbackGiven({});
      }
    } finally { setAnalyzing(false); }
  };

  const submitFeedback = async (index, type) => {
    const res = await fetch(`${API}/api/sales/opportunities/${oppId}/expansion/feedback`, {
      method: 'POST', credentials: 'include', headers: getHeaders(),
      body: JSON.stringify({ recommendation_index: index, feedback_type: type }),
    });
    if (res.ok) setFeedbackGiven(prev => ({ ...prev, [index]: type }));
  };

  const score = analysis?.expansion_score;
  const scoreConfig = SCORE_CONFIG[score] || SCORE_CONFIG.NONE;
  const recs = analysis?.recommendations || [];
  const displayRecs = showAll ? recs : recs.slice(0, 3);
  const projectData = analysis?.project_data || {};
  const confirmed = projectData.confirmed || {};
  const inferred = projectData.inferred?.items || [];
  const futureProjects = analysis?.future_projects || [];

  return (
    <div data-testid="expansion-panel" style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          Expansion Engine
        </h3>
        <button data-testid="run-expansion-btn" onClick={runAnalysis} disabled={analyzing}
          style={{ padding: '4px 10px', background: analyzing ? '#f1f5f9' : '#c0392b', color: analyzing ? '#94a3b8' : '#fff',
            border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: analyzing ? 'wait' : 'pointer' }}>
          {analyzing ? 'Analyzing...' : score ? 'Re-Analyze' : 'Analyze'}
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 12, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Loading...</div>
      ) : !score ? (
        <div style={{ padding: 14, background: '#f8fafc', borderRadius: 8, textAlign: 'center' }}>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 8px' }}>No expansion analysis yet</p>
          <button onClick={runAnalysis} disabled={analyzing}
            style={{ padding: '6px 14px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
          </button>
        </div>
      ) : (
        <>
          {/* Score Badge */}
          <div data-testid="expansion-score" style={{ padding: '10px 12px', background: scoreConfig.bg, borderRadius: 8, marginBottom: 10, border: `1px solid ${scoreConfig.color}30` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: scoreConfig.color }}>
                {scoreConfig.icon} {scoreConfig.label}
              </span>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>{recs.length} recommendation{recs.length !== 1 ? 's' : ''}</span>
            </div>
            <p style={{ fontSize: 11, color: scoreConfig.color, margin: '4px 0 0', opacity: 0.85, lineHeight: 1.4 }}>
              {analysis.expansion_score_reason}
            </p>
          </div>

          {/* Primary Recommendation */}
          {analysis.primary_recommendation && (
            <div style={{ padding: '8px 10px', background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a', marginBottom: 10, fontSize: 12, color: '#92400e', lineHeight: 1.4 }}>
              <strong>Next Step:</strong> {analysis.primary_recommendation}
            </div>
          )}

          {/* Recommendations */}
          {displayRecs.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {displayRecs.map((rec, i) => {
                const rel = REL_COLORS[rec.relevance] || REL_COLORS.LOW;
                const fb = feedbackGiven[i];
                return (
                  <div key={i} data-testid={`expansion-rec-${i}`} style={{ padding: '10px 10px', background: '#fff', borderRadius: 8,
                    border: '1px solid #e2e8f0', marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{rec.service}</span>
                      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, fontWeight: 700, background: rel.bg, color: rel.color }}>
                        {rec.relevance}
                      </span>
                    </div>
                    <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 6px', lineHeight: 1.4 }}>{rec.reason}</p>
                    <div style={{ padding: '6px 8px', background: '#f0fdf4', borderRadius: 5, border: '1px solid #bbf7d0', marginBottom: 6 }}>
                      <p style={{ fontSize: 11, color: '#166534', margin: 0, fontStyle: 'italic', lineHeight: 1.3 }}>
                        "{rec.discovery_question}"
                      </p>
                      <p style={{ fontSize: 9, color: '#94a3b8', margin: '3px 0 0' }}>{rec.recommended_timing}</p>
                    </div>
                    {/* Feedback */}
                    {fb ? (
                      <div style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
                        Marked: <strong>{FEEDBACK_OPTIONS.find(f => f.key === fb)?.label || fb}</strong>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {FEEDBACK_OPTIONS.map(fo => (
                          <button key={fo.key} data-testid={`fb-${i}-${fo.key}`} onClick={() => submitFeedback(i, fo.key)}
                            style={{ padding: '2px 6px', fontSize: 9, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3,
                              color: '#64748b', cursor: 'pointer', fontWeight: 500 }}>
                            {fo.icon} {fo.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {recs.length > 3 && !showAll && (
                <button onClick={() => setShowAll(true)}
                  style={{ width: '100%', padding: '6px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
                    fontSize: 11, color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>
                  Show {recs.length - 3} more
                </button>
              )}
            </div>
          )}

          {/* Project Data Toggle */}
          <button data-testid="toggle-project-data" onClick={() => setShowProject(!showProject)}
            style={{ width: '100%', padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
              fontSize: 11, color: '#475569', cursor: 'pointer', fontWeight: 600, textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Extracted Project Data</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ transform: showProject ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          {showProject && (
            <div style={{ marginTop: 6, padding: '8px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Confirmed</p>
                <button data-testid="edit-project-data-btn"
                  onClick={() => {
                    if (editingProject) { setEditingProject(false); }
                    else { setEditForm({ ...confirmed }); setEditingProject(true); setProjectSaved(false); }
                  }}
                  style={{ padding: '2px 8px', fontSize: 9, fontWeight: 600, borderRadius: 3, cursor: 'pointer',
                    border: `1px solid ${editingProject ? '#dc2626' : '#c0392b'}`,
                    background: editingProject ? '#dc2626' : '#fff', color: editingProject ? '#fff' : '#c0392b',
                  }}>{editingProject ? 'Cancel' : 'Edit'}</button>
              </div>

              {editingProject ? (
                <ProjectDataEditor
                  confirmed={confirmed}
                  editForm={editForm}
                  setEditForm={setEditForm}
                  saving={savingProject}
                  saved={projectSaved}
                  onSave={async () => {
                    setSavingProject(true);
                    try {
                      const res = await fetch(`${API}/api/sales/opportunities/${oppId}/expansion/project-data`, {
                        method: 'PUT', credentials: 'include', headers: getHeaders(),
                        body: JSON.stringify({ confirmed: editForm }),
                      });
                      if (res.ok) {
                        const result = await res.json();
                        setProjectSaved(true);
                        setTimeout(() => { setEditingProject(false); setProjectSaved(false); fetchAnalysis(); }, 1500);
                      }
                    } finally { setSavingProject(false); }
                  }}
                />
              ) : (
                Object.entries(confirmed).filter(([, v]) => v !== null && v !== '' && v !== 'unknown' && !(Array.isArray(v) && v.length === 0)).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #f8fafc' }}>
                    <span style={{ color: '#64748b' }}>{k.replace(/_/g, ' ')}</span>
                    <span style={{ color: '#1e293b', fontWeight: 500, maxWidth: 140, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
                    </span>
                  </div>
                ))
              )}

              {inferred.length > 0 && !editingProject && (
                <>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', margin: '8px 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Inferred</p>
                  {inferred.map((item, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#1e293b' }}>{item.fact}</span>
                        <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: '#fef9c3', color: '#a16207' }}>{item.confidence}</span>
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Basis: {item.basis}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Future Projects */}
          {futureProjects.length > 0 && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#2563eb', margin: '0 0 4px', textTransform: 'uppercase' }}>Future Projects</p>
              {futureProjects.map((fp, i) => (
                <div key={i} style={{ fontSize: 11, color: '#1e40af', marginBottom: 4 }}>
                  <strong>{fp.service}</strong> — {fp.approximate_timing}
                  <div style={{ fontSize: 10, color: '#6b7280' }}>{fp.action}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


/* ── Inline Project Data Editor ── */
const PROJECT_FIELDS = {
  linear_footage: { label: 'Linear Footage', type: 'number' },
  fence_type: { label: 'Fence Type', type: 'text' },
  fence_height: { label: 'Fence Height', type: 'text' },
  gates_count: { label: 'Gates Count', type: 'number' },
  gate_sizes: { label: 'Gate Sizes', type: 'text' },
  gate_automation: { label: 'Gate Automation', type: 'toggle' },
  acreage: { label: 'Acreage', type: 'number' },
  property_type: { label: 'Property Type', type: 'select', options: ['residential', 'commercial', 'ranch', 'farm', 'industrial', 'unknown'] },
  clearing_needed: { label: 'Clearing Needed', type: 'toggle' },
  concrete_needed: { label: 'Concrete Needed', type: 'toggle' },
  color_paint: { label: 'Color / Paint', type: 'text' },
  terrain_notes: { label: 'Terrain Notes', type: 'text' },
  permit_required: { label: 'Permit Required', type: 'toggle' },
};

function ProjectDataEditor({ confirmed, editForm, setEditForm, saving, saved, onSave }) {
  const set = (k, v) => setEditForm(prev => ({ ...prev, [k]: v }));

  // Get all field keys (existing + known project fields)
  const allKeys = [...new Set([...Object.keys(confirmed), ...Object.keys(PROJECT_FIELDS)])];

  const fieldStyle = { width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };

  return (
    <div data-testid="project-data-editor" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {allKeys.map(k => {
        const cfg = PROJECT_FIELDS[k] || { label: k.replace(/_/g, ' '), type: 'text' };
        const val = editForm[k] ?? confirmed[k] ?? '';

        if (cfg.type === 'toggle') {
          return (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
              <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{cfg.label}</span>
              <button data-testid={`edit-pd-${k}`} onClick={() => set(k, !editForm[k])}
                style={{ padding: '2px 8px', fontSize: 10, borderRadius: 3, border: '1px solid #d1d5db', cursor: 'pointer',
                  background: editForm[k] ? '#16a34a' : '#f1f5f9', color: editForm[k] ? '#fff' : '#64748b', fontWeight: 600 }}>
                {editForm[k] ? 'Yes' : 'No'}
              </button>
            </div>
          );
        }

        if (cfg.type === 'select') {
          return (
            <div key={k}>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{cfg.label}</label>
              <select data-testid={`edit-pd-${k}`} value={editForm[k] ?? ''} onChange={e => set(k, e.target.value)} style={fieldStyle}>
                {cfg.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          );
        }

        return (
          <div key={k}>
            <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{cfg.label}</label>
            <input data-testid={`edit-pd-${k}`} type={cfg.type} value={editForm[k] ?? ''} onChange={e => set(k, cfg.type === 'number' ? (e.target.value ? parseFloat(e.target.value) : '') : e.target.value)}
              style={fieldStyle} />
          </div>
        );
      })}

      <button data-testid="save-project-data-btn" onClick={onSave} disabled={saving || saved}
        style={{ marginTop: 4, padding: '6px 12px', fontSize: 11, fontWeight: 700, border: 'none', borderRadius: 5, cursor: saving ? 'default' : 'pointer',
          background: saved ? '#16a34a' : '#c0392b', color: '#fff', width: '100%' }}>
        {saved ? 'Saved — Re-Analyzing...' : saving ? 'Saving...' : 'Save Corrections & Re-Analyze'}
      </button>
      <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>Changes auto-trigger expansion re-analysis</div>
    </div>
  );
}
